import type { ComponentPropsWithRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useClientsWithTrashed } from '~/api/clients';
import { Card, Icon, InfoBlock, Separator, Text } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';
import { formatVatNumber } from '~/utils/string';

import { bodyStyle, detailsStyle, headerStyle, linesStyle, logoStyle, nameStyle, rootStyle } from './ClientCard.css';

type Props = Omit<ComponentPropsWithRef<typeof Card.Root>, 'render' | 'size' | 'variant'> & {
  clientId: number | null;
  contactId: number | null;
};

export const ClientCard = ({ clientId, contactId, ...delegated }: Props) => {
  const { data: clients } = useClientsWithTrashed();

  const client = clients.find(({ id }) => id === clientId);
  const contact = client?.contacts.find(({ id }) => id === contactId);

  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['clients', 'invoices']);

  if (!client || !contact) {
    return (
      <Card.Root {...delegated} className={clsx(rootStyle, delegated.className)} size="small" variant="primary-accent">
        <Text>{t('invoices:details.summary.client.noClient')}</Text>
      </Card.Root>
    );
  }

  const organisation = client.clientType === 'organisation' ? client : null;
  const { address } = contact;

  const invoiceEmails = contact.emails.filter(({ messages }) => messages.includes('invoices & credit notes'));
  const hasContactInfo = organisation !== null || client.peppolId !== null || invoiceEmails.length > 0;

  return (
    <Card.Root {...delegated} className={clsx(rootStyle, delegated.className)} size="small" variant="primary-accent">
      <div className={headerStyle}>
        <span className={logoStyle}>
          <Icon name={organisation !== null ? 'Company' : 'Person'} size={33} />
        </span>

        <div className={detailsStyle}>
          <span className={nameStyle}>{organisation?.name ?? (client.isInformalAssociation ? client.name : contact.fullName)}</span>

          {organisation !== null && organisation.vatNumber !== null && <span>{formatVatNumber(organisation.vatNumber)}</span>}
          {organisation !== null && organisation.otherNumber !== null && <span>{organisation.otherNumber}</span>}
        </div>
      </div>

      <div className={bodyStyle}>
        <InfoBlock label={t('clients:card.address')} variant="caps">
          <Text className={linesStyle}>
            <span>
              {address.street} {address.number}
            </span>

            <span>
              {address.zipCode} {address.city}
            </span>

            <span>{displayNameOfCountry(address.countryCode)}</span>
          </Text>
        </InfoBlock>

        {hasContactInfo && (
          <>
            <Separator />

            <InfoBlock label={t('clients:contact')} variant="caps">
              <Text className={linesStyle}>
                {organisation !== null && <span>{contact.fullName}</span>}

                {client.peppolId !== null ? (
                  <span>{t('invoices:createEdit.clientInfo.peppol')}</span>
                ) : (
                  invoiceEmails.map(({ email }) => <span key={email}>{email}</span>)
                )}
              </Text>
            </InfoBlock>
          </>
        )}
      </div>
    </Card.Root>
  );
};
